import React from 'react'
import { noop, debounce } from 'lodash'
import { connect } from 'react-redux'
import type { primitives } from '@adc/blueprint'
import * as blueprint from '@adc/blueprint'
import { Listing } from '@adc/sdk/types'
import classnames from 'classnames/bind'
import * as graph from '@adc/sdk'
import {
  Button,
  Segment,
  Text,
  Modal,
  ScrollSpy,
  Icon,
  TutorialTooltip,
  CardContainer,
  Alert,
} from '@adc/ui-adc'
import {
  CombinedListingType,
  FeatureFlags,
  useListingContext,
} from 'common/context/listing'
import { shouldShowSealedBidTreatment } from 'common/utils/privateSeller'
import { AccountRestrictedAlert } from '../../components/accountRestrictedAlert'
import { BidDepositRequiredMessage } from '../../components/bidDepositRequiredMessage'
import {
  eventDetailsUrl,
  getRegistrationRestrictedValues,
} from '../../utils/listing'
import PopupTooltip from '../../components/popupTooltip'
import {
  REGISTER_TO_BID,
  REGISTERED_FOR_AUCTION,
  AUCTION_DETAILS_CTA_ID,
  SAVE_PROPERTY_CTA,
  VIEW_SIMILAR_PROPERTIES,
} from '../../statics'
import {
  RESERVE_TOOLTIP_CONTENT_1,
  RESERVE_TOOLTIP_CONTENT_2,
} from '../../constants/tooltip'
import FirstLookModalButton from '../firstLookModalButton'
import analytics from '../../services/analytics'
import { withModalHooks } from '../../hooks/useModalFeatures'
import { withNudgeHook } from '../../hooks/useNudgesFeatures'
import { toggleOptinModal } from '../../actions/modals'
import { SealedBidCallToAction } from '../sealedBid'
import { srpDynamicSimilarPropertiesLinkPath } from '../sealedBid/constants'
// @ts-ignore no types
import styles from './index.scss'
import { handleSaveProperty } from './common'

export { handleSaveProperty }

const cx = classnames.bind(styles)

export interface RowDataProps {
  label: React.ReactNode
  value?: React.ReactNode
  displayBlock?: boolean
}

const RowData = ({ label, value, displayBlock }: RowDataProps) => {
  return label ? (
    <div className={cx('row-data')}>
      <div className={cx('row', { 'horizontal-row': !displayBlock })}>
        <div className={cx('label', { center: !value })}>{label}</div>
        {!!value && <div className={cx('value')}>{value}</div>}
      </div>
    </div>
  ) : (
    <></>
  )
}

interface RegistrationAccountRestrictedAlertProps {
  user: primitives.AuctionUser
}

const RegistrationAccountRestrictedAlert = ({
  user,
}: RegistrationAccountRestrictedAlertProps) => {
  const { listing } = useListingContext()
  const accountRestrictedAlertValues = getRegistrationRestrictedValues({
    listing,
    user,
  })
  return (
    <>
      {accountRestrictedAlertValues &&
        accountRestrictedAlertValues?.isRegistrationButtonDisabled && (
          <AccountRestrictedAlert
            showIcon={false}
            shouldShowRegistrationLimitReachedMessage={
              accountRestrictedAlertValues.shouldShowRegistrationLimitReachedMessage
            }
          />
        )}
    </>
  )
}

interface MessageContentContainerProps {
  featureFlags: FeatureFlags
  accountRestrictedSlot?: React.ReactNode // this is needed to support different restriction messages (such as direct offer restriction vs registration restriction)
  enableBidDepositRequiredMessage?: boolean
  shouldShowSealedBidTreatment: boolean
  user?: primitives.AuctionUser
}

export const MessageContentContainer = ({
  featureFlags,
  user,
  accountRestrictedSlot = user ? (
    <RegistrationAccountRestrictedAlert user={user} />
  ) : (
    <></>
  ), // default account restricted alert is for registration, other components can also pass empty fragment to not show it OR use a different alert with different conditions
  enableBidDepositRequiredMessage = false,
  shouldShowSealedBidTreatment,
}: MessageContentContainerProps) => {
  const { listing } = useListingContext()

  // this is based off the logic within the RegistrationButton component below where it shows a message rather than the FirstLookModalButton which previously housed these messages.
  // this exists just to preserve previous cases of this message being shown or not shown while changing the location of where it's shown
  const hideMessageContent =
    listing.is_current_user_registered_for_online_auction ||
    listing.current_user_online_registration?.registration_status ===
      graph.enums.OnlineRegistrationStatus.PendingDepositConfirmation

  return hideMessageContent ? (
    <></>
  ) : (
    <>
      {accountRestrictedSlot}
      <BidDepositRequiredMessage
        enableBidDepositRequiredMessage={enableBidDepositRequiredMessage}
        featureFlags={featureFlags}
        listing={listing}
        shouldShowSealedBidTreatment={shouldShowSealedBidTreatment}
      />
    </>
  )
}

interface AuctionDetailsTemplateProps {
  title: string
  primaryButton?: any
  secondaryButton?: any
  description?: React.ReactNode
  isTPS?: boolean
  shouldShowSealedBidTreatment?: boolean
  messageContent?: React.ReactNode
  detailContent?: RowDataProps[]
  additionalContent?: React.ReactNode
  headerContent?: React.ReactNode
}

const AuctionDetailsTemplate = ({
  headerContent,
  primaryButton,
  secondaryButton,
  description,
  title,
  detailContent,
  shouldShowSealedBidTreatment,
  additionalContent,
  isTPS,
  messageContent,
}: AuctionDetailsTemplateProps) => (
  <>
    <TutorialTooltip
      elmId='auction_details_tutorial_tooltip'
      tutorial={4}
      position='left center'
      header='Get Auction Info'
      content='Find out when this auction will take place and where to bid.'
    >
      {/* Hiding the border here as the border is not used directly - but instead by the contents of this thing... */}
      <CardContainer padding='none' style={{ border: 'none' }}>
        <Segment.Group>
          {headerContent && <Segment textAlign='left'>{headerContent}</Segment>}
          <Segment
            secondary
            textAlign='center'
            className={cx(`auction-details-primary`)}
            id={AUCTION_DETAILS_CTA_ID}
          >
            <Text
              color='gray'
              elmId='auction-detail-box-status'
              size='large'
              weight='medium'
              className={cx({
                'u-mb-3': primaryButton || secondaryButton || description,
              })}
            >
              {title}
            </Text>
            {primaryButton && (
              <div
                className={cx({
                  'u-mb-1': secondaryButton || description,
                  'large-fixed-button': primaryButton?.size === 'large',
                })}
              >
                {primaryButton}
              </div>
            )}
            {secondaryButton && (
              <div
                className={cx({
                  'u-mb-1': !!description,
                  'large-fixed-button': secondaryButton?.size === 'large',
                })}
              >
                {secondaryButton}
              </div>
            )}
            {description && <div>{description}</div>}
          </Segment>
          {detailContent && (
            <Segment
              textAlign='center'
              className={cx({
                'status-container': shouldShowSealedBidTreatment,
              })}
            >
              {messageContent && messageContent}
              <div>
                {!shouldShowSealedBidTreatment &&
                  detailContent.map((content, index) => (
                    <RowData key={index} {...content} />
                  ))}
              </div>
              {additionalContent}
            </Segment>
          )}
        </Segment.Group>
      </CardContainer>
    </TutorialTooltip>
    <div data-ad-space={`PDP_AUCTION_DETAILS_BOX_${isTPS ? 'TPS' : 'REO'}`} />
  </>
)

interface RegistrationButtonProps {
  featureFlags?: FeatureFlags
  options?: { size?: string; elmId?: string }
}

// TODO: This method is stuff shovelled on top of stuff shovelled on top of stuff. It needs to be refactored.
export const RegistrationButton = ({
  featureFlags = {},
  options = {},
}: RegistrationButtonProps) => {
  const { listing, segment } = useListingContext()

  const { size = 'large', elmId = 'register_auction_button' } = options

  if (shouldShowSealedBidTreatment({ segment, featureFlags })) {
    return <SealedBidCallToAction elmId='bid_box' />
  }

  if (
    listing.is_current_user_registered_for_online_auction &&
    segment?.segment_status !== graph.enums.OnlineAuctionSegmentStatus.Ended
  ) {
    return (
      <Text
        size='big'
        color='adcLightGreen'
        weight='heavy'
        data-elm-id='register_auction_label'
      >
        <Icon name='check-circle' iconStyle='solid' color='adcLightGreen' />
        {REGISTERED_FOR_AUCTION}
      </Text>
    )
  }

  if (
    listing.current_user_online_registration?.registration_status ===
    graph.enums.OnlineRegistrationStatus.PendingDepositConfirmation
  ) {
    return (
      <Text
        size='big'
        color='caution'
        weight='heavy'
        data-elm-id='registration_pending_deposit_confirmation'
      >
        <Icon name='calendar-clock' iconStyle='solid' color='caution' />
        Your Bid Deposit is Pending
      </Text>
    )
  }

  return (
    <FirstLookModalButton
      mode='registration'
      size={size}
      text={REGISTER_TO_BID}
      elmId={elmId}
      listing={listing}
    />
  )
}

// TODO: these types shouldn't be copied but the actual type should be exported from DS and used here
interface BaseSavePropertyButtonProps {
  listing: Listing
  property?: any
  isSaved?: boolean
  disabled?: boolean
  actions?: { toggleSaveProperty?: any }
  options?: {
    text?: string
    role?:
      | 'primary'
      | 'secondary'
      | 'tertiary'
      | 'brand'
      | 'navigation'
      | 'navigation-light'
      | undefined
    size?: 'large' | 'small' | 'medium' | 'marshmallow' | undefined
    elmId?: string
  }
  pushAuthQueueItem?: any
  pushOnboardingQueueItem?: any
  toggleAuthModal?: any
  nudgeOptInModal?: any
}

export const BaseSavePropertyButton = ({
  listing,
  property = {},
  isSaved,
  disabled = false,
  actions = {},
  options = {},
  pushAuthQueueItem,
  pushOnboardingQueueItem,
  toggleAuthModal,
  nudgeOptInModal,
}: BaseSavePropertyButtonProps) => {
  const {
    text = SAVE_PROPERTY_CTA,
    role = 'primary',
    size = 'large',
    elmId = 'save_property_button',
  } = options

  const { toggleSaveProperty } = actions

  const debounceOnClickSave = debounce(
    async (property: any, toggleSaveProperty: any) => {
      await toggleSaveProperty({
        listing,
        property,
        pushAuthQueueItem,
        toggleAuthModal,
        pushOnboardingQueueItem,
        nudgeOptInModal,
      })
    },
    500
  )

  const onClick = () => {
    toggleSaveProperty && debounceOnClickSave(property, toggleSaveProperty)
  }

  const btnRole:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'brand'
    | 'navigation'
    | 'navigation-light'
    | undefined = isSaved ? 'secondary' : role
  return (
    <Button
      role={btnRole}
      size={size}
      onClick={onClick || noop}
      disabled={disabled}
      elmId={elmId}
      className={cx('remote-bid-button')}
    >
      {isSaved ? 'Property Saved!' : text}
    </Button>
  )
}

interface SavePropertyButtonProps {
  listing: Listing
  featureFlags?: any
  user?: any
  isSaved?: boolean
  disabled?: boolean
  property?: any
  actions?: any
  options?: any
  pushAuthQueueItem?: any
  pushOnboardingQueueItem?: any
  toggleAuthModal?: any
  nudgeOptInModal?: any
}

export const SavePropertyButton = connect(
  (store: any) => ({
    featureFlags: store.featureFlags,
    user: store.user,
  }),
  (dispatch: any) => ({
    openOptInModal: () => dispatch(toggleOptinModal(true)),
  })
)(withNudgeHook(withModalHooks(BaseSavePropertyButton)))

export const BaseSavePropertyIconButton = ({
  listing,
  property = {},
  isSaved,
  actions = {},
  options = {},
  pushAuthQueueItem,
  pushOnboardingQueueItem,
  toggleAuthModal,
  nudgeOptInModal = () => false,
}: SavePropertyButtonProps) => {
  const { saveText, savedText, elmId = 'save_property_button' } = options

  const { toggleSaveProperty } = actions

  const debounceOnClickSave = debounce(
    async (property: any, toggleSaveProperty: any) => {
      await toggleSaveProperty({
        listing,
        property,
        pushAuthQueueItem,
        toggleAuthModal,
        pushOnboardingQueueItem,
        nudgeOptInModal,
      })
    },
    500
  )

  const onClick = () => {
    toggleSaveProperty && debounceOnClickSave(property, toggleSaveProperty)
  }
  return (
    <Button
      className={cx('no-border-button', 'save-button', 'redicon-outline')}
      role='secondary'
      tag='a'
      onClick={onClick || noop}
      elmId={`${elmId}_icon`}
    >
      <Icon
        name='heart'
        iconStyle={isSaved ? 'solid' : 'regular'}
        color='error'
        size='big'
      />
      <Text color='error'>{isSaved ? savedText : saveText}</Text>
    </Button>
  )
}

export const SavePropertyIconButton = connect(
  (store: any) => ({
    featureFlags: store.featureFlags,
    user: store.user,
  }),
  (dispatch: any) => ({
    openOptInModal: () => dispatch(toggleOptinModal(true)),
  })
)(withNudgeHook(withModalHooks(BaseSavePropertyIconButton)))

interface RemoteBidButtonProps {
  isUserRemoteBidQualified: boolean
  listingId: string
}

export const RemoteBidButton = ({
  listingId,
  isUserRemoteBidQualified,
}: RemoteBidButtonProps) => {
  const onClick = () => {
    analytics.remoteBidding.joinTheAuctionRemoteBidButtonClick()
    window.location.assign(`/dashboard/remote-bidding?listingId=${listingId}`)
  }

  return isUserRemoteBidQualified ? (
    <Button
      className={cx('remote-bid-button')}
      size='large'
      elmId='remote_bid_button'
      role='primary'
      onClick={onClick}
    >
      Place a Bid
    </Button>
  ) : (
    <Alert
      content='In order to get qualified for remote bidding, navigate to this property in the Auction.com mobile application.'
      role='info'
      showIcon
    />
  )
}

interface SimilarPropertiesLinkProps {
  listing: CombinedListingType
  isButton?: boolean
  hasSimilarProperties?: boolean
}

export const similarPropertiesLink = ({
  listing,
  isButton = false,
  hasSimilarProperties = true,
}: SimilarPropertiesLinkProps) =>
  isButton ? (
    <ScrollSpy selector='#similar_properties'>
      {({ scrollTo }: { scrollTo: () => void }) => (
        <Button
          role='primary'
          onClick={
            hasSimilarProperties
              ? scrollTo
              : () => {
                  window.location.assign(
                    srpDynamicSimilarPropertiesLinkPath(listing)
                  )
                }
          }
          data-elm-id='view_similar_properties_button'
        >
          {VIEW_SIMILAR_PROPERTIES}
        </Button>
      )}
    </ScrollSpy>
  ) : (
    <Text className='u-mt-2'>
      <ScrollSpy selector='#similar_properties'>
        {({ scrollTo }: { scrollTo: () => void }) => (
          <a
            onClick={
              hasSimilarProperties
                ? scrollTo
                : () => {
                    window.location.assign(
                      srpDynamicSimilarPropertiesLinkPath(listing)
                    )
                  }
            }
            data-elm-id='view_similar_properties_link'
          >
            {VIEW_SIMILAR_PROPERTIES}
          </a>
        )}
      </ScrollSpy>
    </Text>
  )

export const whySaveLink = () => (
  <Modal
    trigger={
      <Text data-elm-id='why_save_lightbox_trigger'>
        <a>Why save?</a>
      </Text>
    }
    title='Why save?'
    mobileTitleSkin='light'
    elmId='why_save_lightbox'
    size='tiny'
  >
    <Modal.Content>
      <Text>When you save a property, you'll be notified if the:</Text>
      <ul>
        <li>credit bid gets posted</li>
        <li>property clears for sale or gets cancelled or postponed</li>
        <li>final sale results are available</li>
      </ul>
    </Modal.Content>
  </Modal>
)

export const remoteBidEducationLink = () => {
  return (
    <>
      <a
        data-elm-id='remote_bid_education_learn_more'
        className={cx('remote-bid-education')}
        href='/lp/remote-bid/'
        target='_blank'
        rel='noreferrer'
      >
        Learn about Remote Bidding
      </a>
    </>
  )
}

export const SAVED_MESSAGE = (
  <span>
    You will receive updates when
    <br />
    new information is available for
    <br />
    this property.
  </span>
)
export const UNSAVED_MESSAGE = (
  <span>
    For updates, save this property to
    <br />
    your dashboard.
  </span>
)

const RESERVE_TOOLTIP = (
  <PopupTooltip
    content={
      <Text
        size='tiny'
        color='white'
        data-elm-id='auction_details_reserve_tooltip_content'
      >
        <p>{RESERVE_TOOLTIP_CONTENT_1}</p>
        <p>{RESERVE_TOOLTIP_CONTENT_2}</p>
      </Text>
    }
    elmId='auction_details_reserve_tooltip'
    position='top left'
    trigger={
      <Icon
        name='info circle'
        data-elm-id='auction_details_reserve_tooltip_icon'
      />
    }
  />
)

export const ReserveNotMet = () => (
  <Text color='caution' weight='heavy'>
    Reserve Not Met {RESERVE_TOOLTIP}
  </Text>
)

export const ReserveNotSet = () => (
  <Text color='caution' weight='heavy'>
    Reserve Not Set {RESERVE_TOOLTIP}
  </Text>
)

export const ReserveMet = () => (
  <Text color='success' weight='heavy'>
    Reserve Met {RESERVE_TOOLTIP}
  </Text>
)

export const EstCreditBidExceeded = () => (
  <Text color='success' weight='heavy'>
    Est. Credit Bid Exceeded
  </Text>
)

export const EstCreditBidNotExceeded = () => (
  <Text color='caution' weight='heavy'>
    Est. Credit Bid Not Exceeded
  </Text>
)

export const EstCreditBidNotSet = () => (
  <Text color='caution' weight='heavy'>
    Est. Credit Bid Not Set
  </Text>
)

export const tpsAdditionalContent = (
  shouldShowSimilarProperties: boolean,
  shouldShowPrepareForAuction = true
) => {
  const { listing } = useListingContext()
  return (
    <div>
      {shouldShowPrepareForAuction && (
        <ScrollSpy selector='#prepare_your_finances'>
          {({ scrollTo }: { scrollTo: any }) => (
            <Text className='u-mt-2'>
              <a onClick={scrollTo} data-elm-id='prepare_for_auction_cta'>
                Prepare for the auction
              </a>
            </Text>
          )}
        </ScrollSpy>
      )}
      {blueprint.listing.utils.isFclOnlineAuction(listing) ? (
        shouldShowSimilarProperties && similarPropertiesLink({ listing })
      ) : (
        <Text className='u-mt-2'>
          <a
            target='_BLANK'
            rel='noreferrer'
            data-elm-id='other_properties_cta'
            href={eventDetailsUrl(listing)}
          >
            Other properties at this auction
          </a>
        </Text>
      )}
    </div>
  )
}

export default AuctionDetailsTemplate
